import { useEffect, useContext, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useLazyGetOrganisationTypesQuery, useGetNotificationsQuery } from '../../../../Redux/Api/Organisations';
import { useAppSelector } from '../../../../Redux/store';
import Button from "../../../Shared/Form/Button";
import Title from '../../../Shared/Title';
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import LoadingPage from "../../LoadingPage";
import { ModalContext } from '../../../Shared/Modal/Modal';
import { useMediaQuery } from '@mui/material';
import { Hydra } from '../../../../Utils/Hydra';
import BusyArea from '../../../Shared/BusyArea';
import StatusPopups from '../../../Shared/Form/StatusPopups';
import { useDeleteNotificationMutation } from "../../../../Redux/Api/Notifications";
import GenericDatagrid from '../../../Datagrid/GenericDatagrid';
import RowActions, { RowAction } from '../../../Datagrid/DatagridLayout/Rows/RowActions';
import { faLink, faMobileAlt, faTag, faTags, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StatusRenderer, { getStatusDetails } from '../../../Datagrid/Renderers/StatusRenderer';

interface Notification {
    id: string;
    title: string;
    data: {
        url: string;
    }
    text: string;
    sentAt: {
        date: string;
    };
    type: string;
    isSent: boolean;
    scheduledAt: {
        date: string
    }
}

const NotifyHistoryPage = () => {
    const { t, i18n } = useTranslation();
    const org_id = useAppSelector((s) => s.organisation.id);
    const { data = [], isFetching, refetch } = useGetNotificationsQuery(org_id!, {
        skip: !org_id,
    }) as { data?: Notification[]; isFetching: boolean; refetch: () => void };
    const navigate = useNavigate();
    const { withModal } = useContext(ModalContext);
    const [getTypes, { data: types }] = useLazyGetOrganisationTypesQuery()
    const isSmallScreen = useMediaQuery('(min-width: 700px)');
    const isMediumScreen = useMediaQuery('(min-width: 900px)');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [deleteNotification] = useDeleteNotificationMutation();

    useEffect(() => {
        if (org_id) {
            refetch();
        }
    }, [org_id, refetch]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const formattedTime = date.toTimeString().split(' ')[0].slice(0, 5);
        return `${day}-${month}-${year} ${t('at')} ${formattedTime}`;
    };

    const handleDelete = async (id: string) => {
        if (busy) return

        setBusy(true)
        setError('')
        setSuccessMessage('')

        try {
            await deleteNotification(id);

            if (org_id)
                refetch()
        } catch (e) {
            let err = e as Hydra.Error
            setError(err['hydra:description'])
        }

        setBusy(false)
    }

    useEffect(() => {
        if (org_id)
            getTypes(org_id)
    }, [org_id])

    const columns = [
        {
            field: 'title',
            headerName: t('notification:tableTitle'),
            flex: 1,
        },
        {
            field: 'data.url',
            headerName: t('organisation:news:url'),
            valueGetter: (params: any) => params.row.data?.url || '',
            flex: 1,
            hide: !isSmallScreen,
        },
        {
            field: 'type',
            headerName: t('notification:tableType'),
            flex: 1,
            valueGetter: (params: any) => {
                if (params.row.type === 'all') {
                    return t('notification:checkbox');
                } else {
                    const type = types?.find((t) => t.id === params.row.type);
                    return type ? type.name : t('notification:typeDeleted');
                }
            },
            renderCell: (params: any) => (
                <span className='w-full overflow-hidden text-ellipsis'><FontAwesomeIcon icon={params.row.type === 'all' ? faTags : faTag} /> {params.value}</span>
            ),
            hide: !isSmallScreen,
        },
        {
            field: 'isSent',
            headerName: t('notification:isSent'),
            flex: 1,
            minWidth: isMediumScreen ? 120 : 50,
            maxWidth: isMediumScreen ? 120 : 50,
            valueGetter: (params: any) => {
                const { text } = getStatusDetails(params.row.isSent ? 'sent' : new Date(params.row.scheduledAt.date) < new Date() ? 'not-sent' : 'waiting-to-send');
                return text;
            },
            renderCell: (params: any) => (
                <StatusRenderer status={params.row.isSent ? 'sent' : new Date(params.row.scheduledAt.date) < new Date() ? 'not-sent' : 'waiting-to-send'} small={!isMediumScreen} />
            ),
        },
        {
            field: 'date',
            headerName: t('notification:tableDate'),
            flex: 1,
            valueGetter: (params: any) => {
                return params.row.isSent ? formatDate(params.row.sentAt.date) : t('notification:datePlanned', { date: formatDate(params.row.scheduledAt.date) });
            },
            hide: !isSmallScreen,
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            renderCell: (params: any) => {
                const actions: RowAction[] = [
                    {
                        label: t('makeNotification'),
                        icon: <FontAwesomeIcon icon={faMobileAlt} />,
                        onClick: () => navigate(`/${i18n.language}/dashboard/notify`, { state: { news: { title: params.row.title, url: params.row.url, text: params.row.text } } }),
                    },
                    {
                        label: t('cards:list:delete'),
                        icon: <FontAwesomeIcon icon={faTrashAlt} />,
                        withModal: {
                            onYes: () => { handleDelete(params.row.id); }
                        }
                    }
                ];


                if (params.row.data.url) {
                    actions.unshift({
                        label: t('openLink'),
                        icon: <FontAwesomeIcon icon={faLink} />,
                        withModal: {
                            title: t('openLinkQuestion') as string,
                            body: params.row.data.url,
                            onYes: () => { window.open(params.row.data.url, '_blank'); }
                        }
                    });
                }

                return <RowActions actions={actions} />;
            },
        }
    ];

    if (!org_id || isFetching || !data || !types) { return <LoadingPage />; }

    return (
        <>
            <div className='flex flex-wrap justify-between items-center mb-3'>
                <Title text={t('notification:history')} subtitle={t('notification:historySubtitle') as string} />
                <Button
                    action={() => navigate(`/${i18n.language}/dashboard/notify`)}
                    className="my-6"
                    title={t('notification:createNotification')}
                    icon={faPlus}
                />
            </div>

            <StatusPopups setText={setError} type='error' text={error} />
            <StatusPopups setText={setSuccessMessage} type='success' text={successMessage} />
            <BusyArea busy={busy}>
                <GenericDatagrid
                    data={data}
                    columns={columns}
                    onRowClick={(params: any) => navigate(`/${i18n.language}/dashboard/notify`, { state: { news: { title: params.row.title, url: params.row.url, text: params.row.text } } })}
                    className='mb-5 lg:mb-10'
                    disableHeaderCells={['date']}
                />
            </BusyArea>
        </>
    );
};

export default NotifyHistoryPage;
